/**
 * @module Auth
 */

/**
 * Convenience function to determine whether the specified user is an individual or organization.
 *
 * @param {object} userProfileInfo - User profile data object.
 *
 * @returns {boolean} Boolean flag indicating whether or not the specified profile is an organization.
 */
function isOrganizationProfile(userProfileInfo) {
  return Boolean(
    userProfileInfo &&
      userProfileInfo['https://www.life.church/user_metadata'] &&
      userProfileInfo['https://www.life.church/user_metadata'].organizationType,
  );
}

/**
 * Convenience function to condense the specified name to first and last name, not taking into account middle/third names.
 *
 * @param {string} name - The user's name.
 *
 * @returns {string} The condensed name value.
 */
function condensedName(name) {
  if (name && name.length > 0) {
    const returnValue = `${name.substring(0, name.indexOf(' ') + 2)}.`;
    if (returnValue.includes(' .')) {
      return returnValue.replace(' .', '');
    }
    return returnValue;
  }
  return '';
}

/**
 * Convenience function to display the user's first name and last initial if
 * the following conditions are met:
 *
 * 1. The user must not have an email address in app_meta given_name and
 * must have something in user_meta first_name.
 * 2. The user does have a first name last name in user_metadata.
 * 3. The user has something in app_metadata.given_name.
 *
 * @param {object} profile - The user profile data object.
 *
 * @returns {string} A user's first name and last initial or an empty string.
 */
function getUserNameFromProfile(profile) {
  if (!profile) {
    return '';
  }

  const userMetadata = profile['https://www.life.church/user_metadata'];
  const isOrganization = isOrganizationProfile(profile);
  const isFbUserNameEmpty =
    profile.sub && profile.sub.startsWith('facebook') && !profile.given_name;

  if (isOrganization) {
    return userMetadata.last_name;
  }
  if (
    (profile.name &&
      profile.name.includes('@') &&
      userMetadata &&
      !userMetadata.first_name) ||
    isFbUserNameEmpty
  ) {
    return '';
  }

  let user_name;
  const isProfileFirstName =
    userMetadata &&
    userMetadata.first_name &&
    userMetadata.first_name.length > 0;
  const isProfileGivenName =
    profile.given_name && profile.given_name.length > 0;

  if (isProfileFirstName) {
    user_name = `${userMetadata.first_name} ${userMetadata.last_name || ''}`;
    return condensedName(user_name);
  }
  if (isProfileGivenName) {
    user_name = `${profile.given_name} ${profile.family_name || ''}`;
    return condensedName(user_name);
  }
  return '';
}

module.exports = {
  condensedName,
  getUserNameFromProfile,
  isOrganizationProfile,
};
